import { useState, useCallback, useMemo, useRef } from 'react';
import {
  createAuthClient,
  AuthClientError,
  LoginResponse,
  AuthClientConfig,
} from '../api/authClient';
import { SessionData } from '../schemas/session';
import { ChallengeData } from '../schemas/challenge';
import { AuthErrorCode } from '../schemas/errors';
import { getStoredZToken, storeZToken, clearZToken } from './useZToken';

export type AuthFlowState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'challenge';
      data: ChallengeData;
      selectedType?: string;
      error?: { code: AuthErrorCode; message: string };
    }
  | { status: 'success'; session: SessionData }
  | { status: 'error'; code: AuthErrorCode; message: string };

export interface UseAuthFlowOptions extends AuthClientConfig {
  onSuccess?: (session: SessionData) => void;
}

export function useAuthFlow(options: UseAuthFlowOptions) {
  const [state, setState] = useState<AuthFlowState>({ status: 'idle' });
  const [trustDevice, setTrustDevice] = useState(false);
  const bypassAttemptedRef = useRef(false);

  const client = useMemo(() => createAuthClient(options), [options.passwordLoginEndpoint]);

  const handleSuccess = useCallback(
    (session: SessionData) => {
      // Store Z_TOKEN if present in response
      if (session.extra?.z?.type === 'z_token') {
        const { token, expires_at } = session.extra.z.data;
        storeZToken(session.user_id, token, expires_at);
      }
      setState({ status: 'success', session });
      options.onSuccess?.(session);
    },
    [options.onSuccess]
  );

  const attemptZTokenBypass = useCallback(
    async (challengeData: ChallengeData): Promise<boolean> => {
      const hasZTokenChallenge = challengeData.challenges.some(
        (c) => c.type.toUpperCase() === 'Z_TOKEN'
      );
      if (!hasZTokenChallenge) return false;

      const storedToken = getStoredZToken(challengeData.user_id);
      if (!storedToken) return false;

      try {
        const response = await client.verifyChallenge(challengeData.token, 'Z_TOKEN', storedToken);
        handleSuccess(response.data);
        return true;
      } catch (error) {
        console.error('[Auth] attemptZTokenBypass - failed:', error);
        clearZToken(challengeData.user_id);
        return false;
      }
    },
    [client, handleSuccess]
  );

  const handleLoginResponse = useCallback(
    async (response: LoginResponse) => {
      if (response.type === 'session') {
        handleSuccess(response.data);
      } else {
        // Attempt Z_TOKEN bypass before showing challenge dialog
        if (!bypassAttemptedRef.current) {
          bypassAttemptedRef.current = true;
          const bypassed = await attemptZTokenBypass(response.data);
          if (bypassed) return;
        }
        setState({ status: 'challenge', data: response.data });
      }
    },
    [handleSuccess, attemptZTokenBypass]
  );

  const handleError = useCallback((error: unknown) => {
    if (error instanceof AuthClientError) {
      setState({ status: 'error', code: error.code, message: error.message });
    } else if (error instanceof Error) {
      setState({
        status: 'error',
        code: 'unauthorized',
        message: error.message,
      });
    } else {
      setState({
        status: 'error',
        code: 'unauthorized',
        message: 'Unable to login. Please try again. If the problem persists, contact support.',
      });
    }
  }, []);

  const login = useCallback(
    async (email: string, password: string) => {
      setState({ status: 'loading' });
      try {
        const response = await client.passwordLogin(email, password);
        await handleLoginResponse(response);
      } catch (error) {
        handleError(error);
      }
    },
    [client, handleLoginResponse, handleError]
  );

  const selectChallengeType = useCallback((type: string) => {
    setState((prev) => {
      if (prev.status !== 'challenge') return prev;
      return { ...prev, selectedType: type, error: undefined };
    });
  }, []);

  const startChallenge = useCallback(
    async (type: string) => {
      if (state.status !== 'challenge') return;

      try {
        await client.startChallenge(state.data.token, type);
        selectChallengeType(type);
      } catch (error) {
        // Keep challenge state open but show error
        const errorInfo =
          error instanceof AuthClientError
            ? { code: error.code, message: error.message }
            : error instanceof Error
              ? {
                  code: 'unauthorized' as AuthErrorCode,
                  message: error.message,
                }
              : {
                  code: 'unauthorized' as AuthErrorCode,
                  message: 'Invalid email or password',
                };

        setState((prev) => {
          if (prev.status !== 'challenge') return prev;
          return { ...prev, error: errorInfo };
        });
      }
    },
    [state, client, selectChallengeType]
  );

  const verifyChallenge = useCallback(
    async (code: string) => {
      if (state.status !== 'challenge' || !state.selectedType) return;

      // Save challenge data before loading
      const challengeData = state.data;
      const selectedType = state.selectedType;

      setState({ status: 'loading' });
      try {
        const response = await client.verifyChallenge(challengeData.token, selectedType, code, {
          requestZToken: trustDevice,
        });
        handleSuccess(response.data);
      } catch (error) {
        // Restore challenge state with error
        if (error instanceof AuthClientError) {
          setState({
            status: 'challenge',
            data: challengeData,
            selectedType,
            error: { code: error.code, message: error.message },
          });
        } else if (error instanceof Error) {
          setState({
            status: 'challenge',
            data: challengeData,
            selectedType,
            error: { code: 'unauthorized', message: error.message },
          });
        }
      }
    },
    [state, client, trustDevice, handleSuccess]
  );

  const reset = useCallback(() => {
    setState({ status: 'idle' });
    setTrustDevice(false);
    bypassAttemptedRef.current = false;
  }, []);

  const goBackToSelection = useCallback(() => {
    setState((prev) => {
      if (prev.status !== 'challenge') return prev;
      return { ...prev, selectedType: undefined, error: undefined };
    });
  }, []);

  return {
    state,
    login,
    selectChallengeType,
    startChallenge,
    verifyChallenge,
    reset,
    goBackToSelection,
    client,
    trustDevice,
    setTrustDevice,
  };
}

export type UseAuthFlowReturn = ReturnType<typeof useAuthFlow>;
